import { FC, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Box,
  Paper,
  Stack,
  alpha,
} from '@mui/material';
import { FamilyRestroom } from '@mui/icons-material';
import { useGuardianState, useGuardianActions } from '../contexts/guardian.context';
import { useNavigate } from 'react-router-dom';
import { pages } from '../../../../utils/common';
import useGuardianChildrenService from '../../../../apis/guardian/sub-apis/useGuardianChildrenService';
import { useTranslation } from 'src/hooks/useTranslationHook';

const ChildSelector: FC = () => {
  const { translateText } = useTranslation();
  const { children, selectedChildId } = useGuardianState();
  const { setChildren, setSelectedChild } = useGuardianActions();
  const navigate = useNavigate();
  const { getChildren } = useGuardianChildrenService();

  useEffect(() => {
    getChildren().then((childrenList) => {
      setChildren(childrenList);
      if (childrenList.length === 1) {
        setSelectedChild(childrenList[0].id);
        navigate(`${pages.guardian.path}${pages.guardian.subPages?.dashboard?.path}`, {
          replace: true,
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChildSelect = (childId: string) => {
    setSelectedChild(childId);
    navigate(`${pages.guardian.path}${pages.guardian.subPages?.dashboard?.path}`, {
      replace: true,
    });
  };

  if (children.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
        data-testid="child-selector-loading"
      >
        <Typography>{translateText('guardian.loadingChildren')}</Typography>
      </Box>
    );
  }

  if (children.length === 1) {
    return null;
  }

  return (
    <Box
      data-testid="child-selector-container"
      sx={{
        width: '100%',
        minHeight: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        px: { xs: 2, sm: 3 },
        py: 4,
        boxSizing: 'border-box',
      }}
    >
      <Stack
        spacing={4}
        alignItems="center"
        sx={{
          width: '100%',
          maxWidth: { xs: '100%', sm: 580 },
          mx: 'auto',
          gap: 4
        }}
      >
        <Paper
          elevation={0}
          data-testid="child-selector-intro"
          sx={(theme) => ({
            p: { xs: 4 },
            width: '100%',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            textAlign: 'center',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.primary.light, 0.06)} 100%)`,
          })}
        >
          <Stack spacing={2} sx={{ gap: 2, alignItems: 'center' }}>
            <Box
              sx={(theme) => ({
                width: 56,
                height: 56,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.15),
                color: 'primary.main',
              })}
              aria-hidden
            >
              <FamilyRestroom sx={{ fontSize: 32 }} />
            </Box>
            <Typography variant="overline" color="primary" sx={{ fontWeight: 700, letterSpacing: 0.08 }}>
              {translateText('guardian.selectChild')}
            </Typography>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              {translateText('guardian.selectChildHeadline')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.65 }}>
              {translateText('guardian.selectChildDescription')}
            </Typography>
          </Stack>
        </Paper>

        <Grid container sx={{ width: '100%', gap: 4 }}>
          {children.map((child) => (
            <Grid item xs={12} sm={5.8} key={child.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  border: selectedChildId === child.id ? 2 : 1,
                  borderColor:
                    selectedChildId === child.id ? 'primary.main' : 'divider',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
                onClick={() => handleChildSelect(child.id)}
                data-testid={`child-card-${child.id}`}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      sx={{ width: 56, height: 56 }}
                      src={child.photoUrl}
                      data-testid={`child-avatar-${child.id}`}
                    >
                      {child.name.charAt(0)}
                    </Avatar>
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="h6">{child.name}</Typography>
                      {child.grade && (
                        <Typography variant="body2" color="text.secondary">
                          {child.grade} {child.section && `- ${child.section}`}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Stack>
    </Box>
  );
};

export default ChildSelector;
